import { Capacitor } from '@capacitor/core';

// AdMob 타입 (동적 import용)
type AdMobModule = typeof import('@capacitor-community/admob');

// 환경 타입
type AdEnvironment = 'android' | 'toss' | 'dev';

// Ad Unit ID (보상형 광고 - AdMob) - 플랫폼별 분기
const REWARDED_AD_UNIT_ID_ANDROID = 'ca-app-pub-5244313691595903/8184986357';
const REWARDED_AD_UNIT_ID_IOS = 'ca-app-pub-5244313691595903/4514162944';
const REWARDED_AD_UNIT_ID = Capacitor.getPlatform() === 'ios'
  ? REWARDED_AD_UNIT_ID_IOS
  : REWARDED_AD_UNIT_ID_ANDROID;

// AppsInToss 광고 그룹 ID (보상형)
const APPS_IN_TOSS_AD_GROUP_ID = 'ait.v2.live.55b8c0acd4da42f4';

// AppsInToss API 타입 정의
interface LoadFullScreenAdEvent {
  type: 'loaded';
}

interface ShowFullScreenAdEvent {
  type: 'requested' | 'show' | 'impression' | 'clicked' | 'dismissed' | 'failedToShow';
}

interface ShowFullScreenAdRewardEvent {
  type: 'userEarnedReward';
  data: {
    unitType: string;
    unitAmount: number;
  };
}

type ShowAdEvent = ShowFullScreenAdEvent | ShowFullScreenAdRewardEvent;

interface LoadFullScreenAdParams {
  options: { adGroupId: string };
  onEvent: (event: LoadFullScreenAdEvent) => void;
  onError: (error: unknown) => void;
}

interface ShowFullScreenAdParams {
  options: { adGroupId: string };
  onEvent: (event: ShowAdEvent) => void;
  onError: (error: unknown) => void;
}

type LoadFullScreenAdFn = ((params: LoadFullScreenAdParams) => () => void) & {
  isSupported?: () => boolean;
};

type ShowFullScreenAdFn = ((params: ShowFullScreenAdParams) => () => void) & {
  isSupported?: () => boolean;
};

class AdService {
  private static instance: AdService;
  private initialized = false;
  private lastAdTime = 0;
  private adCooldown = 10000; // 10초 쿨다운
  private environment: AdEnvironment = 'dev';

  // AdMob
  private admobModule: AdMobModule | null = null;
  private adInFlight = false;

  // AppsInToss
  private loadFullScreenAd: LoadFullScreenAdFn | null = null;
  private showFullScreenAd: ShowFullScreenAdFn | null = null;
  private appsInTossReady = false;
  private cleanupFn: (() => void) | null = null;

  private constructor() {}

  static getInstance(): AdService {
    if (!AdService.instance) {
      AdService.instance = new AdService();
    }
    return AdService.instance;
  }

  /**
   * Check if running in AppsInToss environment
   */
  private isAppsInTossEnvironment(): boolean {
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('toss') || ua.includes('appintoss')) {
        return true;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__APPS_IN_TOSS__ || (window as any).AppsInToss) {
        return true;
      }
    }
    return false;
  }

  /**
   * 현재 실행 환경 감지
   */
  private detectEnvironment(): AdEnvironment {
    // 1. AppsInToss 환경 우선 체크 (Toss 앱 내 WebView)
    if (this.isAppsInTossEnvironment()) {
      console.log('[AdService] AppsInToss environment detected');
      return 'toss';
    }

    // 2. Android 네이티브 환경 체크
    if (Capacitor.isNativePlatform()) {
      console.log('[AdService] Native platform detected (Android)');
      return 'android';
    }

    // 3. 그 외는 개발 환경
    console.log('[AdService] Development environment detected');
    return 'dev';
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    // 환경 감지
    this.environment = this.detectEnvironment();
    console.log('[AdService] Environment:', this.environment);

    switch (this.environment) {
      case 'toss':
        await this.initializeAppsInToss();
        break;

      case 'android':
        await this.initializeAdMob();
        break;

      case 'dev':
        console.log('[AdService] Using mock ads (development mode)');
        this.initialized = true;
        break;
    }
  }

  /**
   * Initialize AppsInToss ads
   */
  private async initializeAppsInToss(): Promise<void> {
    try {
      // 동적 import (Vite 호환)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Module may not be installed
      const framework = await import('@apps-in-toss/web-framework');

      if (framework.loadFullScreenAd && framework.showFullScreenAd) {
        this.loadFullScreenAd = framework.loadFullScreenAd as unknown as LoadFullScreenAdFn;
        this.showFullScreenAd = framework.showFullScreenAd as unknown as ShowFullScreenAdFn;

        // Check if API is supported
        const isLoadSupported = this.loadFullScreenAd.isSupported?.() !== false;
        const isShowSupported = this.showFullScreenAd.isSupported?.() !== false;

        if (isLoadSupported && isShowSupported) {
          this.initialized = true;
          console.log('[AdService] AppsInToss initialized');
          this.preloadAppsInTossAd();
          return;
        }
      }

      console.log('[AdService] AppsInToss API not supported');
      this.initialized = true;
    } catch (e) {
      console.error('[AdService] AppsInToss init failed:', e);
      this.initialized = true;
    }
  }

  /**
   * Initialize AdMob for Android
   */
  private async initializeAdMob(): Promise<void> {
    try {
      // 동적 import
      this.admobModule = await import('@capacitor-community/admob');
      const { AdMob } = this.admobModule;

      await AdMob.initialize({
        initializeForTesting: false,
      });

      this.initialized = true;
      console.log('[AdService] AdMob initialized for Android');

      // 광고 미리 로드
      this.preloadAdMob();
    } catch (e) {
      console.error('[AdService] AdMob init failed:', e);
      this.initialized = true;
    }
  }

  /**
   * Preload AppsInToss ad
   */
  private preloadAppsInTossAd(): void {
    if (!this.loadFullScreenAd) return;

    this.cleanupFn?.();
    this.cleanupFn = null;
    this.appsInTossReady = false;

    console.log('[AdService] Preloading AppsInToss ad:', APPS_IN_TOSS_AD_GROUP_ID);

    try {
      const cleanup = this.loadFullScreenAd({
        options: { adGroupId: APPS_IN_TOSS_AD_GROUP_ID },
        onEvent: (event) => {
          console.log('[AdService] AppsInToss load event:', event.type);
          if (event.type === 'loaded') {
            this.appsInTossReady = true;
            console.log('[AdService] AppsInToss ad ready');
          }
        },
        onError: (error) => {
          console.error('[AdService] AppsInToss load failed:', error);
          this.appsInTossReady = false;
        },
      });

      this.cleanupFn = cleanup;
    } catch (error) {
      console.error('[AdService] AppsInToss preload exception:', error);
    }
  }

  /**
   * Preload AdMob ad
   */
  private async preloadAdMob(): Promise<void> {
    if (!this.admobModule) return;

    try {
      const { AdMob } = this.admobModule;
      await AdMob.prepareRewardVideoAd({
        adId: REWARDED_AD_UNIT_ID,
        isTesting: false,
      });
      console.log('[AdService] AdMob rewarded ad preloaded');
    } catch (e) {
      console.error('[AdService] AdMob preload failed:', e);
    }
  }

  isAvailable(): boolean {
    if (!this.initialized) return false;

    // 쿨다운 체크
    const now = Date.now();
    if (now - this.lastAdTime < this.adCooldown) {
      return false;
    }

    return true;
  }

  async showRewardedAd(): Promise<boolean> {
    console.log('[AdService] showRewardedAd called, environment:', this.environment);

    // 재진입 가드: 로드 중 연타로 동시 2회 진입하면 플러그인 전역 리스너가
    // 이중 등록되어 광고 1회에 보상 2회가 지급되거나 표시가 깨짐
    if (this.adInFlight) {
      console.log('[AdService] ad already in flight, ignoring');
      return false;
    }
    this.adInFlight = true;

    try {
      const show = (() => {
        switch (this.environment) {
          case 'toss':
            return this.showAppsInTossRewardedAd();
          case 'android':
            return this.showAdMobRewardedAd();
          case 'dev':
          default:
            return this.showMockAd();
        }
      })();
      // 안전망: SDK 이벤트 유실로 영원히 pending이면 호출부 버튼이 영구 잠김 — 3분 후 실패 처리
      const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 180_000));
      return await Promise.race([show, timeout]);
    } catch (e) {
      console.error('[AdService] showRewardedAd exception:', e);
      return false;
    } finally {
      this.adInFlight = false;
    }
  }

  /**
   * Show AppsInToss rewarded ad
   */
  private showAppsInTossRewardedAd(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.showFullScreenAd || !this.loadFullScreenAd) {
        console.log('[AdService] AppsInToss not initialized');
        resolve(false);
        return;
      }

      // If not ready, try to load first
      if (!this.appsInTossReady) {
        console.log('[AdService] AppsInToss ad not ready, loading...');
        this.loadAppsInTossAdAsync()
          .then((loaded) => {
            if (loaded) {
              this.showAppsInTossRewardedAd().then(resolve);
            } else {
              resolve(false);
            }
          })
          .catch(() => resolve(false)); // 로드 예외 시에도 반드시 settle — 아니면 버튼 영구 잠김
        return;
      }

      let rewarded = false;
      let showCleanup: (() => void) | null = null;
      const finish = (result: boolean) => {
        showCleanup?.(); // show 구독 해제 (미해제 시 show 1회당 구독 누적)
        showCleanup = null;
        this.preloadAppsInTossAd();
        resolve(result);
      };

      try {
        showCleanup = this.showFullScreenAd({
          options: { adGroupId: APPS_IN_TOSS_AD_GROUP_ID },
          onEvent: (event) => {
            console.log('[AdService] AppsInToss show event:', event.type);

            switch (event.type) {
              case 'userEarnedReward':
                rewarded = true;
                if ('data' in event) {
                  console.log('[AdService] AppsInToss reward earned:', event.data);
                }
                break;

              case 'dismissed':
                if (rewarded) {
                  this.lastAdTime = Date.now();
                }
                finish(rewarded);
                break;

              case 'failedToShow':
                finish(false);
                break;
            }
          },
          onError: (error) => {
            console.error('[AdService] AppsInToss show failed:', error);
            finish(false);
          },
        });
      } catch (error) {
        console.error('[AdService] AppsInToss show exception:', error);
        resolve(false);
      }
    });
  }

  /**
   * Load AppsInToss ad async
   */
  private loadAppsInTossAdAsync(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.loadFullScreenAd) {
        resolve(false);
        return;
      }

      this.cleanupFn?.();
      this.cleanupFn = null;
      this.appsInTossReady = false;

      try {
        const cleanup = this.loadFullScreenAd({
          options: { adGroupId: APPS_IN_TOSS_AD_GROUP_ID },
          onEvent: (event) => {
            if (event.type === 'loaded') {
              this.appsInTossReady = true;
              resolve(true);
            }
          },
          onError: (error) => {
            console.error('[AdService] AppsInToss load failed:', error);
            resolve(false);
          },
        });

        this.cleanupFn = cleanup;
      } catch (error) {
        // 동기 throw 시 executor가 reject되어 호출부 Promise가 영원히 pending되던 버그
        console.error('[AdService] AppsInToss load exception:', error);
        resolve(false);
        return;
      }

      // Timeout after 5 seconds
      setTimeout(() => {
        if (!this.appsInTossReady) {
          console.log('[AdService] AppsInToss load timeout');
          resolve(false);
        }
      }, 5000);
    });
  }

  /**
   * Show AdMob rewarded ad (Android)
   */
  private async showAdMobRewardedAd(): Promise<boolean> {
    if (!this.initialized || !this.admobModule) {
      console.error('[AdService] AdMob not initialized');
      return false;
    }

    const { AdMob, RewardAdPluginEvents } = this.admobModule;

    return new Promise((resolve) => {
      let rewarded = false;
      let rewardHandle: { remove: () => Promise<void> } | null = null;
      let dismissHandle: { remove: () => Promise<void> } | null = null;
      let failHandle: { remove: () => Promise<void> } | null = null;

      const cleanup = async () => {
        if (rewardHandle) await rewardHandle.remove();
        if (dismissHandle) await dismissHandle.remove();
        if (failHandle) await failHandle.remove();
        // 다음 광고 미리 로드
        this.preloadAdMob();
      };

      // async IIFE + 전체 try/catch — addListener 실패 시에도 반드시 settle (미처리 시 Promise 영구 pending)
      (async () => {
        try {
          // 보상 수령 리스너
          rewardHandle = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (reward) => {
            console.log('[AdService] AdMob user rewarded:', reward);
            rewarded = true;
          });

          // 광고 종료 리스너
          dismissHandle = await AdMob.addListener(RewardAdPluginEvents.Dismissed, async () => {
            // Wait briefly for Rewarded event to fire (race condition fix)
            if (!rewarded) await new Promise(r => setTimeout(r, 300));
            console.log('[AdService] AdMob ad dismissed, rewarded:', rewarded);
            await cleanup();
            if (rewarded) {
              this.lastAdTime = Date.now();
            }
            resolve(rewarded);
          });

          // 광고 실패 리스너
          failHandle = await AdMob.addListener(RewardAdPluginEvents.FailedToShow, async (error) => {
            console.error('[AdService] AdMob failed to show:', error);
            await cleanup();
            resolve(false);
          });

          // 광고 로드 및 표시
          await AdMob.prepareRewardVideoAd({
            adId: REWARDED_AD_UNIT_ID,
            isTesting: false,
          });
          await AdMob.showRewardVideoAd();
        } catch (e: unknown) {
          console.error('[AdService] AdMob show failed:', e);
          await cleanup();
          resolve(false);
        }
      })();
    });
  }

  /**
   * Show mock ad (development) — fullscreen overlay with 3s countdown
   */
  private showMockAd(): Promise<boolean> {
    return new Promise((resolve) => {
      console.log('[AdService] Mock ad shown (dev only)');

      const overlay = document.createElement('div');
      overlay.style.cssText = [
        'position:fixed', 'inset:0', 'z-index:99999',
        'background:rgba(0,0,0,0.92)',
        'display:flex', 'flex-direction:column',
        'align-items:center', 'justify-content:center',
        'font-family:system-ui,sans-serif',
      ].join(';');

      // AD label
      const adLabel = document.createElement('div');
      adLabel.textContent = 'AD';
      adLabel.style.cssText = [
        'font-size:13px', 'font-weight:700', 'letter-spacing:3px',
        'color:#aaa', 'border:1px solid #555',
        'padding:3px 10px', 'border-radius:4px',
        'margin-bottom:28px',
      ].join(';');

      // Main box
      const box = document.createElement('div');
      box.style.cssText = [
        'width:260px', 'height:160px',
        'background:#1a1a2a',
        'border:2px solid #3a3a5a',
        'border-radius:12px',
        'display:flex', 'align-items:center', 'justify-content:center',
      ].join(';');
      const boxText = document.createElement('div');
      boxText.style.cssText = 'color:#555;font-size:14px;letter-spacing:1px;';
      boxText.textContent = 'ADVERTISEMENT';
      box.appendChild(boxText);

      // Countdown ring
      const countWrap = document.createElement('div');
      countWrap.style.cssText = [
        'position:relative', 'width:64px', 'height:64px',
        'margin-top:28px',
      ].join(';');

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '64');
      svg.setAttribute('height', '64');
      svg.setAttribute('viewBox', '0 0 64 64');
      svg.style.cssText = 'position:absolute;inset:0;transform:rotate(-90deg);';

      const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      track.setAttribute('cx', '32');
      track.setAttribute('cy', '32');
      track.setAttribute('r', '26');
      track.setAttribute('fill', 'none');
      track.setAttribute('stroke', '#333');
      track.setAttribute('stroke-width', '4');

      const progress = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      const circumference = 2 * Math.PI * 26;
      progress.setAttribute('cx', '32');
      progress.setAttribute('cy', '32');
      progress.setAttribute('r', '26');
      progress.setAttribute('fill', 'none');
      progress.setAttribute('stroke', '#a855f7');
      progress.setAttribute('stroke-width', '4');
      progress.setAttribute('stroke-linecap', 'round');
      progress.setAttribute('stroke-dasharray', String(circumference));
      progress.setAttribute('stroke-dashoffset', '0');

      svg.appendChild(track);
      svg.appendChild(progress);
      countWrap.appendChild(svg);

      const numLabel = document.createElement('div');
      numLabel.style.cssText = [
        'position:absolute', 'inset:0',
        'display:flex', 'align-items:center', 'justify-content:center',
        'font-size:22px', 'font-weight:700', 'color:#fff',
      ].join(';');
      numLabel.textContent = '3';
      countWrap.appendChild(numLabel);

      // Close button (disabled during countdown)
      const closeBtn = document.createElement('button');
      closeBtn.style.cssText = [
        'position:fixed', 'top:20px', 'right:20px',
        'width:36px', 'height:36px',
        'border-radius:50%',
        'border:2px solid #555',
        'background:#222', 'color:#888',
        'font-size:16px', 'cursor:not-allowed',
        'display:flex', 'align-items:center', 'justify-content:center',
        'opacity:0.4',
      ].join(';');
      closeBtn.textContent = 'X';
      closeBtn.disabled = true;

      overlay.appendChild(adLabel);
      overlay.appendChild(box);
      overlay.appendChild(countWrap);
      overlay.appendChild(closeBtn);
      document.body.appendChild(overlay);

      const TOTAL_MS = 3000;
      const START = Date.now();

      const frame = () => {
        const elapsed = Date.now() - START;
        const fraction = Math.min(elapsed / TOTAL_MS, 1);
        const left = Math.max(0, Math.ceil(TOTAL_MS / 1000 - elapsed / 1000));

        // animate ring
        progress.setAttribute('stroke-dashoffset', String(circumference * (1 - fraction)));
        numLabel.textContent = String(left === 0 ? 0 : left);

        if (fraction < 1) {
          requestAnimationFrame(frame);
        } else {
          // countdown done — enable close
          closeBtn.disabled = false;
          closeBtn.style.cursor = 'pointer';
          closeBtn.style.opacity = '1';
          closeBtn.style.color = '#fff';
          closeBtn.style.borderColor = '#fff';
          numLabel.textContent = '0';

          // auto-resolve as rewarded
          const finish = () => {
            overlay.remove();
            this.lastAdTime = Date.now();
            resolve(true);
          };
          closeBtn.addEventListener('click', finish);
        }
      };

      requestAnimationFrame(frame);

      // X before countdown ends = cancel
      closeBtn.addEventListener('click', () => {
        if (closeBtn.disabled) return;
        // handled above in finish()
      });
    });
  }
}

// 싱글톤 내보내기
export const adService = AdService.getInstance();
export default AdService;
